import React, { ReactElement, ReactNode, useLayoutEffect, useMemo, useState } from "react";
import List from "@mui/material/List";
import Collapse from "@mui/material/Collapse";
import { darken, ListItemButton, ListItemButtonProps, ListItemIcon } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import ListItemText from "@mui/material/ListItemText";
import TranslationWrapper from "../../../../../components/TranslationWrapper/TranslationWrapper";
import { useAppSelector } from "../../../../../store";
import NavigationItem from "./NavigationItem";
import { useLocation } from "react-router";
import { warningColor } from "../../../../../assets/jss/material-dashboard-pro-react";
import AuthenticationWrapper from "../../../../../auth/AuthenticationWrapper";

function isReactElement(child: ReactNode): child is ReactElement {
  return React.isValidElement(child);
}

function isNavItem(child: ReactElement): child is ReactElement<typeof NavigationItem> {
  return child.type === NavigationItem;
}

export default function NavigationGroup({
  auth,
  text,
  icon,
  children,
  ButtonProps = {},
}: {
  auth: string | null;
  text: { tKey: string; tDefault: string } | ReactElement | string;
  icon?: ReactElement;
  children: ReactNode;
  ButtonProps?: Omit<ListItemButtonProps, "onClick">;
}) {
  const [open, setOpen] = useState(false);
  const mini = useAppSelector((state) => state.app.sidebarMiniActive);

  let renderedText;
  if (typeof text === "object" && "tKey" in text) {
    renderedText = <TranslationWrapper {...text} />;
  } else {
    renderedText = text;
  }
  const location = useLocation();

  const active = useMemo(() => {
    return React.Children.toArray(children).some(
      (child) =>
        isReactElement(child) && isNavItem(child) && "to" in child.props && child.props.to === location.pathname
    );
  }, [children, location.pathname]);

  useLayoutEffect(() => {
    if (active && !open) {
      setOpen(true);
    }
  }, [active, open]);

  const color = active ? darken(warningColor[0], 0.2) : undefined;

  const element = (
    <List disablePadding>
      <ListItemButton onClick={() => setOpen((o) => !o)} {...ButtonProps}>
        <ListItemIcon
          sx={{
            color,
            minWidth: "32px",
            transition: "all 200ms ease-in",
          }}
        >
          {icon ?? null}
        </ListItemIcon>
        <ListItemText
          primary={renderedText}
          sx={{
            color,
            transition: "all 200ms ease-in",
            opacity: mini ? 0 : 1,
            ".MuiTypography-root": { fontSize: "14px" },
          }}
        />
        <ExpandMore
          sx={{
            color,
            transition: "all 200ms ease-in",
            transform: open ? "rotate(-180deg)" : "rotate(0deg)",
          }}
        />
      </ListItemButton>
      <Collapse in={open} mountOnEnter>
        <List
          disablePadding
          sx={{
            ml: 2,
          }}
        >
          {children}
        </List>
      </Collapse>
    </List>
  );

  if (auth) {
    return <AuthenticationWrapper permissionKey={auth}>{element}</AuthenticationWrapper>;
  }

  return element;
}
